import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { Avatar, Typography } from '@navi/web-ui/lib/primitives';
import styles from '../../index.module.scss';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from 'src/components/SkeletonLoader/LineLoader';
import cx from 'classnames';
import { formatDateTime } from 'src/utils/commonUtils';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import { useNavigate } from 'react-router-dom';
import { setSelectedIdApprovalItem } from '../../reducers/ApprovalsSlice';
import { PENDING_APPROVALS_KEY, initialPaginationParams } from '../../constant';
import { IPendingApprovalItem } from '../../interface';

const PendingApprovalListItem: React.FC<{
  approvalItem: IPendingApprovalItem;
}> = ({ approvalItem }) => {
  const selectedApprovalItem = useSelector(
    (state: RootState) => state.approvals.selectedIdApprovalItem
  );
  const createdAt = approvalItem.createdAt;
  const { name } = approvalItem.makerDetails;
  const { optimizedDocumentUri } = approvalItem.data;
  const { [PENDING_APPROVALS_KEY]: queryParams } = readQueryParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSelectApprovalItem = () => {
    dispatch(setSelectedIdApprovalItem(approvalItem));
    const params = {
      ...initialPaginationParams,
      ...queryParams,
      selectedApprovalItem: approvalItem.referenceId
    };
    const url = createQueryParams({ [PENDING_APPROVALS_KEY]: params });
    navigate(url, {
      replace: true
    });
  };

  return (
    <div
      className={cx([
        styles.listItem,
        {
          [styles.listItem__active]: approvalItem.referenceId === selectedApprovalItem?.referenceId
        }
      ])}
      onClick={handleSelectApprovalItem}
    >
      <Avatar src={optimizedDocumentUri} isImage size={36} />
      <div>
        <span title={name}>
          <Typography className={styles.sideBarName} variant={'p3'}>
            {name}
          </Typography>
        </span>
        <Typography variant={'p5'} className={styles.timestamp}>
          {formatDateTime(createdAt)}
        </Typography>
      </div>
    </div>
  );
};

const ItemLoader = () => {
  return (
    <div className={styles.listItem}>
      <LineLoader lineStyle={styles.loaderAvatar} />
      <div>
        <LineLoader lineWidth={200} dimenshionUnit="px" numberOfLines={2} childrenType={'P5'} />
      </div>
    </div>
  );
};

const PendingApprovalList: React.FC = () => {
  const list = useSelector((state: RootState) => state.approvals.idCardList);
  const loading = useSelector((state: RootState) => state.approvals.loading);
  return (
    <div className={styles.listContainer}>
      <SuspenseLoader
        loading={loading}
        fallBack={
          <div>
            {Array.from({ length: 10 }, (_, index) => (
              <ItemLoader key={index} />
            ))}
          </div>
        }
      >
        {list.length ? (
          list.map(item => <PendingApprovalListItem approvalItem={item} key={item.referenceId} />)
        ) : (
          <Typography variant={'p3'} className={styles.emptyListText}>
            No search results found
          </Typography>
        )}
      </SuspenseLoader>
    </div>
  );
};

export default PendingApprovalList;
